import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";

import { SearchBox } from "@/features/search/SearchBox";
import { useAuth } from "@/hooks/authContext";
import { useMotionVariants } from "@/hooks/usePrefersReducedMotion";
import { cn } from "@/lib/format";
import { EASE } from "@/lib/motion";

const NAV = [
  { to: "/trending", label: "Trending" },
  { to: "/map", label: "Map" },
  { to: "/bookmarks", label: "Saved" },
];

/**
 * Two hamburger lines that rotate into an X. Lines are absolutely positioned so
 * the morph is a pure transform interpolation, never a re-mount.
 */
function HamburgerMorph({ open }: { open: boolean }) {
  const morph = {
    transition: { duration: 0.4, ease: EASE },
  };
  return (
    <span className="relative block h-3.5 w-5" aria-hidden>
      <motion.span
        className="absolute left-0 top-0 h-[1.5px] w-full rounded-full bg-current"
        animate={open ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
        {...morph}
      />
      <motion.span
        className="absolute bottom-0 left-0 h-[1.5px] w-full rounded-full bg-current"
        animate={open ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
        {...morph}
      />
    </span>
  );
}

/**
 * App shell.
 *
 * The nav is a floating glass island detached from the viewport top — not an
 * edge-to-edge bar — so the page reads as layered physical planes. On mobile
 * it expands into a full-screen blur overlay with a staggered link reveal.
 */
export function AppLayout() {
  const location = useLocation();
  const { isSignedIn, me, isModerator } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const variants = useMotionVariants({
    hidden: { opacity: 0, y: 28, filter: "blur(8px)" },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { duration: 0.7, ease: EASE, delay: 0.08 * i },
    }),
  });
  const reduced = useReducedMotion();

  const isHome = location.pathname === "/";

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (menuOpen) {
      const { overflow } = document.body.style;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = overflow;
      };
    }
  }, [menuOpen]);

  const mobileLinks = [
    ...NAV,
    ...(isModerator ? [{ to: "/admin", label: "Admin" }] : []),
    {
      to: isSignedIn && me?.profile ? `/u/${me.profile.username}` : "/auth",
      label: isSignedIn ? "My profile" : "Sign in",
    },
  ];

  return (
    <div className="min-h-[100dvh] bg-bg">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50
          focus:rounded-full focus:bg-accent focus:px-4 focus:py-2 focus:text-black"
      >
        Skip to content
      </a>

      <div className="mx-auto max-w-content px-4">
        <motion.header
          initial={{ y: -24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="glass sticky top-6 z-40 mt-6 flex items-center gap-3 rounded-full border
            border-border/80 py-2 pl-5 pr-2 shadow-card"
        >
          <Link to="/" className="flex shrink-0 items-center gap-2" aria-label="Khaabo home">
            <span
              aria-hidden
              className="grid h-7 w-7 place-items-center rounded-full bg-accent/15 text-sm text-accent"
            >
              ◍
            </span>
            <span className="font-display text-xl text-text">Khaabo</span>
          </Link>

          {!isHome && (
            <div className="hidden min-w-0 flex-1 md:block">
              <SearchBox size="md" />
            </div>
          )}

          <nav className="ml-auto hidden items-center gap-1 md:flex" aria-label="Main">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "rounded-full px-3.5 py-2 text-sm transition-colors duration-300",
                    isActive ? "bg-accent/10 text-accent" : "text-muted hover:text-text",
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}

            {isModerator && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  cn(
                    "rounded-full px-3.5 py-2 text-sm transition-colors duration-300",
                    isActive ? "bg-accent/10 text-accent" : "text-muted hover:text-text",
                  )
                }
              >
                Admin
              </NavLink>
            )}

            {isSignedIn && me?.profile ? (
              <Link
                to={`/u/${me.profile.username}`}
                className="ml-1 rounded-full bg-surface-2/80 px-4 py-2 text-sm text-text
                  transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]
                  hover:-translate-y-[1px]"
              >
                {me.profile.username}
              </Link>
            ) : (
              <Link
                to="/auth"
                className="group ml-1 flex items-center gap-2 rounded-full bg-accent px-4 py-2
                  text-sm font-semibold text-black transition-transform duration-300
                  ease-[cubic-bezier(0.32,0.72,0,1)] hover:-translate-y-[1px] active:scale-[0.98]"
              >
                Sign in
              </Link>
            )}
          </nav>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-label="Menu"
            className="ml-auto grid h-10 w-10 place-items-center rounded-full text-muted
              transition-colors duration-300 hover:bg-surface-2 hover:text-text md:hidden"
          >
            <HamburgerMorph open={menuOpen} />
          </button>
        </motion.header>
      </div>

      {!isHome && (
        <div className="mx-auto max-w-content px-4 pb-2 pt-6 md:hidden">
          <SearchBox size="md" />
        </div>
      )}

      <main id="main">
        <Outlet />
      </main>

      {/* Full-screen glass overlay: heavy blur, staggered mask reveal. */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.25 } }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed inset-0 z-50 flex flex-col bg-bg/80 backdrop-blur-3xl md:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
          >
            <div className="mx-auto flex w-full max-w-content items-center justify-between px-4 py-8">
              <span className="font-display text-2xl text-text">Khaabo</span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
                className="grid h-11 w-11 place-items-center rounded-full border border-border
                  text-muted transition-colors duration-300 hover:text-text"
              >
                <HamburgerMorph open={menuOpen} />
              </button>
            </div>

            <nav aria-label="Mobile navigation" className="flex-1 overflow-y-auto px-4">
              <ul className="space-y-2">
                {mobileLinks.map((item, index) => (
                  <motion.li
                    key={item.to}
                    custom={index}
                    variants={variants}
                    initial="hidden"
                    animate="visible"
                  >
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        cn(
                          "block rounded-[1.75rem] px-6 py-4 font-display text-3xl transition-colors",
                          isActive ? "bg-accent/10 text-accent" : "text-text hover:bg-surface-2/70",
                        )
                      }
                    >
                      {item.label}
                    </NavLink>
                  </motion.li>
                ))}
              </ul>
            </nav>

            <p className="px-4 pb-10 pt-6 text-xs text-subtle">
              Dish-first food discovery · Kolkata
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <footer className="mt-24">
        <div className="mx-auto max-w-content px-4 py-16">
          <div className="flex flex-wrap items-start justify-between gap-10">
            <div className="max-w-sm">
              <p className="font-display text-2xl text-text">Khaabo</p>
              <p className="mt-3 text-sm leading-relaxed text-muted">
                Dish-first food discovery. Rankings are computed from stored review evidence,
                and we say “not enough data” rather than guessing.
              </p>
              <p className="mt-6 text-sm text-muted">
                Made by{" "}
                <a
                  href="https://www.linkedin.com/in/dipalok-chakraborty/"
                  className="font-medium text-accent underline decoration-accent/40
                    underline-offset-4 transition-colors hover:decoration-accent"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Dipalok Chakraborty
                </a>
              </p>
            </div>

            <div className="text-sm">
              <p className="mb-3 text-[10px] uppercase tracking-[0.2em] text-subtle">Data</p>
              <ul className="space-y-2 text-muted">
                <li>
                  Places ©{" "}
                  <a
                    href="https://www.openstreetmap.org/copyright"
                    className="text-accent hover:underline"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    OpenStreetMap
                  </a>{" "}
                  contributors
                </li>
                <li>Discussion from Reddit and YouTube public APIs</li>
                <li>Reviews from Khaabo users</li>
              </ul>
            </div>
          </div>

          <p className="mt-12 text-xs text-subtle">
            Launching in Kolkata. Built with open data and free tooling.
          </p>
        </div>
      </footer>

      {/* Fixed film-grain: one physical texture over the whole page. */}
      {reduced ? null : (
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 z-[45] opacity-[0.03] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />
      )}
    </div>
  );
}
